<template>
  <div>
    <page-title-bar :customTitle="customTitle"></page-title-bar>
    <v-container fluid grid-list-xl pt-0>
      <v-layout row wrap justify-center>
        <v-flex xs12>
          <v-toolbar :elevation="0" height="auto">
            <v-layout row justify-space-between align-center style="padding: 32px">
              <!-- Month Picker -->
              <v-flex xs12 md3>
                <div style="max-width: 290px">
                  <v-menu :close-on-content-click="false" v-model="menu_month_picker" :nudge-right="40" lazy
                    transition="scale-transition" offset-y full-width min-width="290px" max-width="290px"
                    :disabled="loader">
                    <v-text-field slot="activator" v-model="chosen_month" label="Chọn tháng" :disabled="loader"
                      prepend-icon="event" readonly hide-details></v-text-field>
                    <v-date-picker min="2025-03" :max="maxMonth" locale="vi-VI" type="month" v-model="chosen_month"
                      @input="menu_month_picker = false"></v-date-picker>
                  </v-menu>
                </div>
              </v-flex>

              <!-- Status Info -->
              <v-flex xs12 md6>
                <v-layout row wrap align-center justify-center>
                  <v-flex shrink>
                    <div class="text-center px-2">
                      <div class="caption grey--text text--darken-1 mb-1">Trạng thái tháng được chọn</div>
                      <v-chip 
                        v-if="is_locked" 
                        color="green" 
                        text-color="white" 
                        small 
                        label
                        class="font-weight-medium"
                      >
                        <v-icon left x-small>check_circle</v-icon>
                        Đã chốt
                      </v-chip>
                      <v-chip 
                        v-else 
                        color="orange" 
                        text-color="white" 
                        small 
                        label
                        class="font-weight-medium"
                      >
                        <v-icon left x-small>schedule</v-icon>
                        Chưa chốt
                      </v-chip>
                    </div>
                  </v-flex>
                  <v-flex shrink>
                    <div class="text-center px-2">
                      <div class="caption grey--text text--darken-1 mb-1">Tháng gần nhất đã chốt</div>
                      <div v-if="latestLockedMonth" class="body-2 font-weight-medium primary--text">
                        {{ formatMonthDisplay(latestLockedMonth) }}
                      </div>
                      <div v-else class="body-2 grey--text">
                        Chưa có
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-flex>

              <!-- Action Button -->
              <v-flex xs12 md3 class="text-right">
                <v-btn color="success" class="mr-2" @click="exportExcel" :loading="exporting" :disabled="loader || !report_data">
                  <v-icon left small>cloud_download</v-icon>
                  Xuất Excel
                </v-btn>

                <v-btn v-if="!is_locked" color="primary" :loading="lockLoader" :disabled="loader || !canLock"
                  @click="confirmLock">
                  <v-icon left small>lock</v-icon>
                  CHỐT BÁO CÁO
                </v-btn>
                <v-btn v-else color="grey" disabled flat>
                  <v-icon left small>lock</v-icon>
                  ĐÃ CHỐT
                </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar>

          <!-- Warning message if can't lock -->
          <v-layout row wrap v-if="!canLock && !is_locked && !loader">
            <v-flex xs12>
              <v-alert :value="true" type="info" outline>
                <strong>Lưu ý:</strong> Cần chốt báo cáo tháng {{ formatMonthDisplay(previousMonth) }} trước khi có thể chốt tháng {{ formatMonthDisplay(chosen_month) }}.
              </v-alert>
            </v-flex>
          </v-layout>

          <div class="mt-3">
            <v-card>
              <v-card-title class="headline">
                Báo cáo kết quả kinh doanh tháng {{ formatMonthDisplay(chosen_month) }}
                <v-spacer></v-spacer>
                <span v-if="locked_at" class="caption grey--text">
                  Chốt lúc: {{ formatDateTime(locked_at) }}
                </span>
              </v-card-title>
              <v-divider></v-divider>

              <!-- Loading State -->
              <v-card-text v-if="loader" class="text-center pa-5">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                <p class="mt-3">Đang tải báo cáo...</p>
              </v-card-text>

              <!-- Report Content -->
              <v-card-text v-else-if="report_data">
                <div class="table-responsive">
                  <table class="business-report-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="min-width: 350px">Chỉ tiêu</th>
                        <th class="text-right" style="min-width: 180px">Số tiền</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- DOANH THU -->
                      <tr class="section-header">
                        <td colspan="2"><strong>DOANH THU</strong></td>
                      </tr>
                      
                      <!-- Doanh thu tiền phòng - Expandable -->
                      <tr class="expandable-row" @click="toggleRoomRevenueDetail">
                        <td>
                          Doanh thu tiền phòng
                          <v-icon small class="ml-1">{{ showRoomRevenueDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right" >{{ formatRevenueValue(report_data.tong_doanh_thu_tien_phong) }}</td>
                      </tr>
                      <template v-if="showRoomRevenueDetail">
                        <!-- Doanh thu tiền phòng chi tiết -->
                        <tr class="sub-row level-1" @click.stop="toggleDetail('roomBill')">
                          <td class="pl-3">
                            - Doanh thu tiền phòng
                            <v-icon v-if="hasDetail('roomBill')" small class="ml-1">{{ showDetail.roomBill ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_phong) }}</td>
                        </tr>
                        <template v-if="showDetail.roomBill && report_data.doanh_thu_tien_phong_detail">
                          <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_phong_detail)" :key="`roomBill_${idx}`" class="sub-row level-2">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                          </tr>
                        </template>

                        <!-- Doanh thu tiền điện -->
                        <tr class="sub-row level-1" @click.stop="toggleDetail('electric')">
                          <td class="pl-3">
                            - Doanh thu tiền điện
                            <v-icon v-if="hasDetail('electric')" small class="ml-1">{{ showDetail.electric ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_dien) }}</td>
                        </tr>
                        <template v-if="showDetail.electric && report_data.doanh_thu_tien_dien_detail">
                          <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_dien_detail)" :key="`electric_${idx}`" class="sub-row level-2">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                          </tr>
                        </template>

                        <!-- Doanh thu tiền nước -->
                        <tr class="sub-row level-1" @click.stop="toggleDetail('water')">
                          <td class="pl-3">
                            - Doanh thu tiền nước
                            <v-icon v-if="hasDetail('water')" small class="ml-1">{{ showDetail.water ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_nuoc) }}</td>
                        </tr>
                        <template v-if="showDetail.water && report_data.doanh_thu_tien_nuoc_detail">
                          <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_nuoc_detail)" :key="`water_${idx}`" class="sub-row level-2">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                          </tr>
                        </template>

                        <!-- Doanh thu tiền rác -->
                        <tr class="sub-row level-1" @click.stop="toggleDetail('trash')">
                          <td class="pl-3">
                            - Doanh thu tiền rác
                            <v-icon v-if="hasDetail('trash')" small class="ml-1">{{ showDetail.trash ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_rac) }}</td>
                        </tr>
                        <template v-if="showDetail.trash && report_data.doanh_thu_tien_rac_detail">
                          <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_rac_detail)" :key="`trash_${idx}`" class="sub-row level-2">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                          </tr>
                        </template>

                        <!-- Doanh thu tiền internet -->
                        <tr class="sub-row level-1" @click.stop="toggleDetail('internet')">
                          <td class="pl-3">
                            - Doanh thu tiền internet
                            <v-icon v-if="hasDetail('internet')" small class="ml-1">{{ showDetail.internet ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_internet) }}</td>
                        </tr>
                        <template v-if="showDetail.internet && report_data.doanh_thu_tien_internet_detail">
                          <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_internet_detail)" :key="`internet_${idx}`" class="sub-row level-2">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                          </tr>
                        </template>

                        <!-- Doanh thu tiền xe máy -->
                        <tr class="sub-row level-1" @click.stop="toggleDetail('motorbike')">
                          <td class="pl-3">
                            - Doanh thu tiền xe máy
                            <v-icon v-if="hasDetail('motorbike')" small class="ml-1">{{ showDetail.motorbike ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_xe_may) }}</td>
                        </tr>
                        <template v-if="showDetail.motorbike && report_data.doanh_thu_tien_xe_may_detail">
                          <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_xe_may_detail)" :key="`motorbike_${idx}`" class="sub-row level-2">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                          </tr>
                        </template>

                        <!-- Doanh thu tiền máy giặt -->
                        <tr class="sub-row level-1" @click.stop="toggleDetail('washing')">
                          <td class="pl-3">
                            - Doanh thu tiền máy giặt
                            <v-icon v-if="hasDetail('washing')" small class="ml-1">{{ showDetail.washing ? 'expand_less' : 'expand_more' }}</v-icon>
                          </td>
                          <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_may_giat) }}</td>
                        </tr>
                        <template v-if="showDetail.washing && report_data.doanh_thu_tien_may_giat_detail">
                          <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_may_giat_detail)" :key="`washing_${idx}`" class="sub-row level-2">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                          </tr>
                        </template>
                      </template>

                      <tr>
                        <td>Điều chỉnh tiền phòng</td>
                        <td class="text-right" >{{ formatRevenueValue(report_data.dieu_chinh_tien_phong) }}</td>
                      </tr>

                      <!-- Doanh thu tiền cọc - Expandable -->
                      <tr class="expandable-row" @click="toggleDetail('deposit')">
                        <td>
                          Doanh thu tiền cọc
                          <v-icon v-if="hasDetail('deposit')" small class="ml-1">{{ showDetail.deposit ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right" >{{ formatRevenueValue(report_data.doanh_thu_tien_coc) }}</td>
                      </tr>
                      <template v-if="showDetail.deposit && report_data.doanh_thu_tien_coc_detail">
                        <tr v-for="(item, idx) in visibleDetails(report_data.doanh_thu_tien_coc_detail)" :key="`deposit_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ item.text }}</td>
                          <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                        </tr>
                      </template>

                      <!-- Chi trả cọc - Expandable -->
                      <tr class="expandable-row" @click="toggleDetail('returnDeposit')">
                        <td>
                          Chi trả cọc
                          <v-icon v-if="hasDetail('returnDeposit')" small class="ml-1">{{ showDetail.returnDeposit ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right" >{{ formatRevenueValue(report_data.chi_tra_coc) }}</td>
                      </tr>
                      <template v-if="showDetail.returnDeposit && report_data.chi_tra_coc_detail">
                        <tr v-for="(item, idx) in visibleDetails(report_data.chi_tra_coc_detail)" :key="`returnDeposit_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ item.text }}</td>
                          <td class="text-right" >{{ formatRevenueValue(item.amount) }}</td>
                        </tr>
                      </template>

                      <tr>
                        <td>Thu khác</td>
                        <td class="text-right" >{{ formatRevenueValue(report_data.thu_khac) }}</td>
                      </tr>
                      <tr class="total-row">
                        <td><strong>TỔNG DOANH THU</strong></td>
                        <td class="text-right" ><strong>{{ formatRevenueValue(report_data.tong_doanh_thu) }}</strong></td>
                      </tr>

                      <!-- CHI PHÍ -->
                      <tr class="section-header">
                        <td colspan="2"><strong>CHI PHÍ</strong></td>
                      </tr>
                      
                      <!-- Chi phí lương và quản lý -->
                      <tr class="expandable-row" @click="toggleDetail('salary')">
                        <td>
                          Chi phí lương và quản lý
                          <v-icon small class="ml-1">{{ showDetail.salary ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_luong_va_quan_ly) }}</td>
                      </tr>
                      <template v-if="showDetail.salary && report_data.chi_phi_luong_detail">
                       <template v-for="(item, idx) in visibleDetails(report_data.chi_phi_luong_detail)">
                          <tr :key="`salary_${idx}`" class="sub-row" :class="{ 'expandable-row': item.details && item.details.length > 0 }" @click="item.details && item.details.length > 0 ? toggleSalarySubDetail(item.key) : null">
                            <td class="pl-5">
                              - {{ item.text }}
                              <v-icon v-if="item.details && item.details.length > 0" small class="ml-1">{{ salarySubDetail[item.key] ? 'expand_less' : 'expand_more' }}</v-icon>
                            </td>
                            <td class="text-right">{{ formatExpenseValue(item.amount) }}</td>
                          </tr>
                          <!-- Sub-breakdown for items with details (e.g., Lương quản lý by building) -->
                          <template v-if="item.details && item.details.length > 0 && salarySubDetail[item.key]">
                            <tr v-for="(detail, dIdx) in item.details" :key="`salary_${idx}_${dIdx}`" class="sub-row level-2">
                              <td style="padding-left: 48px;">• {{ detail.text }}</td>
                              <td class="text-right">{{ formatExpenseValue(detail.amount) }}</td>
                            </tr>
                          </template>
                        </template>
                      </template>

                      <!-- Chi phí thuê đất -->
                      <tr class="expandable-row" @click="toggleDetail('landRent')">
                        <td>
                          Chi phí thuê đất
                          <v-icon small class="ml-1">{{ showDetail.landRent ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_thue_dat) }}</td>
                      </tr>
                      <template v-if="showDetail.landRent && report_data.chi_phi_thue_dat_detail">
                        <tr v-for="(item, idx) in visibleDetails(report_data.chi_phi_thue_dat_detail)" :key="`landRent_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ item.text }}</td>
                          <td class="text-right">{{ formatExpenseValue(item.amount) }}</td>
                        </tr>
                      </template>

                      <!-- Chi phí điện -->
                      <tr class="expandable-row" @click="toggleDetail('expElectric')">
                        <td>
                          Chi phí điện
                          <v-icon v-if="hasDetail('expElectric')" small class="ml-1">{{ showDetail.expElectric ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_dien) }}</td>
                      </tr>
                      <template v-if="showDetail.expElectric && report_data.chi_phi_dien_detail">
                        <tr v-for="(item, idx) in visibleDetails(report_data.chi_phi_dien_detail)" :key="`expElectric_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ item.text }}</td>
                          <td class="text-right">{{ formatExpenseValue(item.amount) }}</td>
                        </tr>
                      </template>

                      <!-- Chi phí nước -->
                      <tr class="expandable-row" @click="toggleDetail('expWater')">
                        <td>
                          Chi phí nước
                          <v-icon v-if="hasDetail('expWater')" small class="ml-1">{{ showDetail.expWater ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_nuoc) }}</td>
                      </tr>
                      <template v-if="showDetail.expWater && report_data.chi_phi_nuoc_detail">
                        <tr v-for="(item, idx) in visibleDetails(report_data.chi_phi_nuoc_detail)" :key="`expWater_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ item.text }}</td>
                          <td class="text-right">{{ formatExpenseValue(item.amount) }}</td>
                        </tr>
                      </template>

                      <tr>
                        <td>Chi phí rác, vệ sinh</td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_rac_ve_sinh) }}</td>
                      </tr>
                      <tr>
                        <td>Chi phí thuế</td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_thue) }}</td>
                      </tr>

                      <!-- Chi phí lãi vay -->
                      <tr class="expandable-row" @click="toggleDetail('loanInterest')">
                        <td>
                          Chi phí lãi vay
                          <v-icon v-if="hasDetail('loanInterest')" small class="ml-1">{{ showDetail.loanInterest ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_lai_vay) }}</td>
                      </tr>
                      <template v-if="showDetail.loanInterest && report_data.chi_phi_lai_vay_detail">
                        <template v-for="(item, idx) in visibleDetails(report_data.chi_phi_lai_vay_detail)">
                          <tr :key="`loanInterest_${idx}`" class="sub-row">
                            <td class="pl-5">- {{ item.text }}</td>
                            <td class="text-right">{{ formatExpenseValue(item.amount) }}</td>
                          </tr>
                          <!-- Sub-breakdown for loan objects within each category -->
                          <template v-if="item.details && item.details.length > 0">
                            <tr v-for="(detail, dIdx) in item.details" :key="`loanInterest_${idx}_${dIdx}`" class="sub-row level-2">
                              <td style="padding-left: 48px;">• {{ detail.text }}</td>
                              <td class="text-right">{{ formatExpenseValue(detail.amount) }}</td>
                            </tr>
                          </template>
                        </template>
                      </template>

                      <tr>
                        <td>Chi phí quảng cáo</td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_quang_cao) }}</td>
                      </tr>
                      <tr>
                        <td>Chi phí Internet</td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_internet) }}</td>
                      </tr>

                      <!-- Chi mua vật tư và sửa chữa -->
                      <tr class="expandable-row" @click="toggleDetail('repair')">
                        <td>
                          Chi mua vật tư và sửa chữa
                          <v-icon small class="ml-1">{{ showDetail.repair ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_mua_vat_tu_va_sua_chua) }}</td>
                      </tr>
                      <template v-if="showDetail.repair && report_data.chi_mua_vat_tu_detail">
                        <tr v-for="(item, idx) in report_data.chi_mua_vat_tu_detail" :key="`repair_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ item.text }}</td>
                          <td class="text-right">{{ formatExpenseValue(item.amount) }}</td>
                        </tr>
                      </template>

                      <tr>
                        <td>Chi phí dẫn phòng</td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_dan_phong) }}</td>
                      </tr>
                      <tr>
                        <td>Chi phí khác</td>
                        <td class="text-right">{{ formatExpenseValue(report_data.chi_phi_khac) }}</td>
                      </tr>
                      <tr class="total-row">
                        <td><strong>TỔNG CHI PHÍ</strong></td>
                        <td class="text-right"><strong>{{ formatExpenseValue(report_data.tong_chi_phi) }}</strong></td>
                      </tr>

                      <!-- LỢI NHUẬN -->
                      <tr class="profit-row">
                        <td><strong>LỢI NHUẬN</strong></td>
                        <td class="text-right" >
                          <strong>{{ formatRevenueValue(report_data.loi_nhuan) }}</strong>
                        </td>
                      </tr>
                      <tr class="profit-row">
                        <td><strong>%</strong></td>
                        <td class="text-right" :class="report_data.ty_suat_loi_nhuan >= 0 ? 'positive-value' : 'negative-value'">
                          <strong>{{ formatPercentage(report_data.ty_suat_loi_nhuan) }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- PHÂN TÍCH TỶ SUẤT LỢI NHUẬN -->
                <div v-if="report_data.phan_tich_ty_suat_loi_nhuan" class="mt-5">
                  <h3 class="mb-3">PHÂN TÍCH TỶ SUẤT LỢI NHUẬN</h3>
                  <div class="table-responsive">
                    <table class="business-report-table margin-table">
                      <thead>
                        <tr>
                          <th class="text-left" style="width: 40px">STT</th>
                          <th class="text-left" style="min-width: 200px"></th>
                          <th class="text-right" style="min-width: 150px">{{ formatMonthDisplay(chosen_month) }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- 1. Doanh thu tiền phòng -->
                        <tr class="margin-header-row">
                          <td rowspan="4">1</td>
                          <td><strong>Doanh thu tiền phòng</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.revenue < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.revenue) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3">Chi phí</td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.expense < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.expense) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>Net</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.profit < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.profit) }}</strong></td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>% Margin</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.margin < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatPercentage(report_data.phan_tich_ty_suat_loi_nhuan.tien_phong.margin) }}</strong></td>
                        </tr>

                        <!-- 2. Doanh thu tiền xe máy -->
                        <tr class="margin-header-row">
                          <td rowspan="4">2</td>
                          <td><strong>Doanh thu tiền xe máy</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.xe_may.revenue < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.xe_may.revenue) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3">Chi phí</td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.xe_may.expense < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.xe_may.expense) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>Net</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.xe_may.profit < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.xe_may.profit) }}</strong></td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>% Margin</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.xe_may.margin < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatPercentage(report_data.phan_tich_ty_suat_loi_nhuan.xe_may.margin) }}</strong></td>
                        </tr>

                        <!-- 3. Doanh thu tiền điện -->
                        <tr class="margin-header-row">
                          <td rowspan="4">3</td>
                          <td><strong>Doanh thu tiền điện</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.dien.revenue < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.dien.revenue) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3">Chi phí</td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.dien.expense < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.dien.expense) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>Net</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.dien.profit < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.dien.profit) }}</strong></td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>% Margin</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.dien.margin < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatPercentage(report_data.phan_tich_ty_suat_loi_nhuan.dien.margin) }}</strong></td>
                        </tr>

                        <!-- 4. Doanh thu tiền nước -->
                        <tr class="margin-header-row">
                          <td rowspan="4">4</td>
                          <td><strong>Doanh thu tiền nước</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.nuoc.revenue < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.nuoc.revenue) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3">Chi phí</td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.nuoc.expense < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.nuoc.expense) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>Net</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.nuoc.profit < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.nuoc.profit) }}</strong></td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>% Margin</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.nuoc.margin < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatPercentage(report_data.phan_tich_ty_suat_loi_nhuan.nuoc.margin) }}</strong></td>
                        </tr>

                        <!-- 5. Doanh thu tiền rác -->
                        <tr class="margin-header-row">
                          <td rowspan="4">5</td>
                          <td><strong>Doanh thu tiền rác</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.rac.revenue < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.rac.revenue) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3">Chi phí</td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.rac.expense < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.rac.expense) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>Net</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.rac.profit < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.rac.profit) }}</strong></td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>% Margin</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.rac.margin < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatPercentage(report_data.phan_tich_ty_suat_loi_nhuan.rac.margin) }}</strong></td>
                        </tr>

                        <!-- 6. Doanh thu tiền internet -->
                        <tr class="margin-header-row">
                          <td rowspan="4">6</td>
                          <td><strong>Doanh thu tiền internet</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.internet.revenue < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.internet.revenue) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3">Chi phí</td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.internet.expense < 0 ? 'negative-value' : ''">{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.internet.expense) }}</td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>Net</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.internet.profit < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatNegativeValue(report_data.phan_tich_ty_suat_loi_nhuan.internet.profit) }}</strong></td>
                        </tr>
                        <tr>
                          <td class="pl-3"><strong>% Margin</strong></td>
                          <td class="text-right" :class="report_data.phan_tich_ty_suat_loi_nhuan.internet.margin < 0 ? 'negative-value' : 'positive-value'"><strong>{{ formatPercentage(report_data.phan_tich_ty_suat_loi_nhuan.internet.margin) }}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-card-text>

              <!-- Error State -->
              <v-card-text v-else class="text-center pa-5">
                <v-icon size="60" color="grey lighten-1">error_outline</v-icon>
                <p class="mt-3 grey--text">Không có dữ liệu báo cáo</p>
              </v-card-text>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import api from "../../api";
import moment from "moment";
import swal from "sweetalert2";
import { ExcelExporter } from '../../helpers/ExcelExporter';

export default {
  filters: {
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return Number(value).toLocaleString("vi-VN");
    },
  },
  data() {
    return {
      loader: false,
      lockLoader: false,
      exporting: false,
      report_data: null,
      is_locked: false,
      locked_at: null,
      locked_by: null,
      locked_months: [],
      menu_month_picker: false,
      chosen_month: moment().subtract(1, "month").format("YYYY-MM"),
      showRoomRevenueDetail: false,
      showDetail: {
        roomBill: false,
        electric: false,
        water: false,
        trash: false,
        internet: false,
        motorbike: false,
        washing: false,
        deposit: false,
        returnDeposit: false,
        salary: false,
        landRent: false,
        expElectric: false,
        expWater: false,
        loanInterest: false,
        repair: false,
      },
      salarySubDetail: {}, // Dynamic toggle for salary sub-items (quan_ly, guard_salary, etc.)
      buildings: [
        { text: "Bảo Ngọc", value: "BN" },
        { text: "Gia Bảo", value: "GB" },
        { text: "TT2", value: "TT2" },
        { text: "TT3", value: "TT3" },
        { text: "MA", value: "MA" },
        { text: "TT", value: "TT" },
      ],
    };
  },
  watch: {
    chosen_month: function () {
      this.getData();
    },
  },
  computed: {
    customTitle() {
      return "Báo cáo kết quả kinh doanh theo tháng";
    },
    maxMonth() {
      return moment().format("YYYY-MM");
    },
    canLock() {
      if (this.is_locked || !this.report_data) return false;
      if (this.chosen_month === "2025-03") return true;
      const previousMonth = moment(this.chosen_month).subtract(1, "month").format("YYYY-MM");
      return this.locked_months.includes(previousMonth);
    },
    previousMonth() {
      return moment(this.chosen_month).subtract(1, "month").format("YYYY-MM");
    },
    latestLockedMonth() {
      if (!this.locked_months || this.locked_months.length === 0) return null;
      return this.locked_months.sort().reverse()[0];
    },
  },
  mounted() {
    this.getLockedMonths();
    this.getData();
  },
  methods: {
    formatMonthDisplay(month) {
      return moment(month).format("MM/YYYY");
    },
    formatDateTime(date) {
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    toggleRoomRevenueDetail() {
      this.showRoomRevenueDetail = !this.showRoomRevenueDetail;
    },
    toggleDetail(key) {
      this.showDetail[key] = !this.showDetail[key];
    },
    toggleSalarySubDetail(key) {
      this.$set(this.salarySubDetail, key, !this.salarySubDetail[key]);
    },
    hasDetail(key) {
      if (!this.report_data) return false;
      const detailMap = {
        roomBill: 'doanh_thu_tien_phong_detail',
        electric: 'doanh_thu_tien_dien_detail',
        water: 'doanh_thu_tien_nuoc_detail',
        trash: 'doanh_thu_tien_rac_detail',
        internet: 'doanh_thu_tien_internet_detail',
        motorbike: 'doanh_thu_tien_xe_may_detail',
        washing: 'doanh_thu_tien_may_giat_detail',
        deposit: 'doanh_thu_tien_coc_detail',
        returnDeposit: 'chi_tra_coc_detail',
        expElectric: 'chi_phi_dien_detail',
        expWater: 'chi_phi_nuoc_detail',
        loanInterest: 'chi_phi_lai_vay_detail',
      };
      const detailKey = detailMap[key];
      if (!detailKey) return false;
      const detail = this.report_data[detailKey];
      return detail && detail.length > 0;
    },
    visibleDetails(details) {
      if (!details) return [];
      return details.filter(item => !item.hidden);
    },
    formatNegativeValue(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num < 0) {
        return `(${Math.abs(num).toLocaleString('vi-VN')})`;
      }
      return num.toLocaleString('vi-VN');
    },
    getBuildingAmount(details, buildingCode) {
      if (!details || !Array.isArray(details)) return 0;
      const found = details.find(item => item.key === buildingCode);
      return found ? found.amount : 0;
    },
    formatPercentage(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (num < 0) {
        return `(${Math.abs(num)}%)`;
      }
      return `${num}%`;
    },
    formatExpenseValue(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      // For expenses: positive values show in parentheses (normal color)
      // negative values show without parentheses
      if (num > 0) {
        return `(${num.toLocaleString('vi-VN')})`;
      }
      return Math.abs(num).toLocaleString('vi-VN');
    },
    formatRevenueValue(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      // For revenue/profit in main report: negative values show in parentheses (normal color)
      // positive values show without parentheses
      if (num < 0) {
        return `(${Math.abs(num).toLocaleString('vi-VN')})`;
      }
      return num.toLocaleString('vi-VN');
    },
    async getLockedMonths() {
      try {
        const result = await api.getLockedBusinessMonths();
        if (result.status === 200) {
          this.locked_months = result.data.locked_months.map(m => m.month);
        }
      } catch (error) {
        console.error("Error loading locked months:", error);
      }
    },
    async getData() {
      this.loader = true;
      this.report_data = null;
      this.is_locked = false;
      this.locked_at = null;
      this.locked_by = null;

      try {
        const result = await api.getMonthlyBusinessReport("month=" + this.chosen_month);

        if (result.status === 200) {
          this.report_data = result.data.report_data;
          this.is_locked = result.data.is_locked || false;
          this.locked_at = result.data.locked_at || null;
          this.locked_by = result.data.locked_by || null;
        } else {
          swal.fire("Lỗi", result.message || "Không thể tải báo cáo", "error");
        }
      } catch (error) {
        console.error("Error loading report:", error);
        swal.fire("Lỗi", "Không thể tải báo cáo", "error");
      }

      this.loader = false;
    },
    async confirmLock() {
      if (this.chosen_month !== "2025-03") {
        const previousMonth = moment(this.chosen_month).subtract(1, "month").format("YYYY-MM");
        if (!this.locked_months.includes(previousMonth)) {
          swal.fire("Không thể chốt", "Cần chốt tháng " + moment(previousMonth).format("MM/YYYY") + " trước", "warning");
          return;
        }
      }

      const confirm = await swal.fire({
        title: "Xác nhận chốt báo cáo",
        html: "Bạn có chắc muốn chốt báo cáo tháng <strong>" + this.formatMonthDisplay(this.chosen_month) + "</strong>?<br><br>Sau khi chốt, số liệu sẽ không thể thay đổi.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Chốt báo cáo",
        cancelButtonText: "Hủy",
        confirmButtonColor: "#1976d2",
      });

      if (confirm.value) {
        await this.lockReport();
      }
    },
    async lockReport() {
      this.lockLoader = true;

      try {
        const result = await api.lockBusinessReport({ month: this.chosen_month });

        if (result.status === 200) {
          swal.fire("Thành công", result.data.message, "success");
          this.getLockedMonths();
          this.getData();
        } else {
          swal.fire("Lỗi", result.message || "Không thể chốt báo cáo", "error");
        }
      } catch (error) {
        console.error("Error locking report:", error);
        var message = (error.response && error.response.data && error.response.data.message) || "Không thể chốt báo cáo";
        swal.fire("Lỗi", message, "error");
      }

      this.lockLoader = false;
    },
    async exportExcel() {
        if (!this.report_data) return;
        this.exporting = true;
        try {
            const data = [];
            
            // --- MAIN REPORT ---
            
            // DOANH THU Header
            data.push({ name: 'DOANH THU', style: { bold: true, background: 'FFFFF3E0' } });

            // Helper to add Revenue Item
            const addRevenueItem = (name, amount, details, level = 1) => {
                data.push({ name, amount, style: { indent: level }, outlineLevel: level });
                if (details) {
                    this.visibleDetails(details).forEach(d => {
                        data.push({ name: d.text, amount: d.amount, style: { indent: level + 1 }, outlineLevel: level + 1 });
                    });
                }
            };

            // Main Items
             // Room
            data.push({ name: 'Doanh thu tiền phòng', amount: this.report_data.tong_doanh_thu_tien_phong, outlineLevel: 0 });
            
            // Detail level 1
             data.push({ name: 'Doanh thu tiền phòng', amount: this.report_data.doanh_thu_tien_phong, style: { indent: 1 }, outlineLevel: 1 });
             if (this.report_data.doanh_thu_tien_phong_detail) {
                 this.visibleDetails(this.report_data.doanh_thu_tien_phong_detail).forEach(d => {
                     data.push({ name: d.text, amount: d.amount, style: { indent: 2 }, outlineLevel: 2 });
                 });
             }

             // Electric
             data.push({ name: 'Doanh thu tiền điện', amount: this.report_data.doanh_thu_tien_dien, style: { indent: 1 }, outlineLevel: 1 });
             if (this.report_data.doanh_thu_tien_dien_detail) {
                 this.visibleDetails(this.report_data.doanh_thu_tien_dien_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 2 }, outlineLevel: 2 }));
             }
             // Water
             data.push({ name: 'Doanh thu tiền nước', amount: this.report_data.doanh_thu_tien_nuoc, style: { indent: 1 }, outlineLevel: 1 });
             if (this.report_data.doanh_thu_tien_nuoc_detail) this.visibleDetails(this.report_data.doanh_thu_tien_nuoc_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 2 }, outlineLevel: 2 }));
             
             // Trash
             data.push({ name: 'Doanh thu tiền rác', amount: this.report_data.doanh_thu_tien_rac, style: { indent: 1 }, outlineLevel: 1 });
             if (this.report_data.doanh_thu_tien_rac_detail) this.visibleDetails(this.report_data.doanh_thu_tien_rac_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 2 }, outlineLevel: 2 }));
             
             // Internet
             data.push({ name: 'Doanh thu tiền internet', amount: this.report_data.doanh_thu_tien_internet, style: { indent: 1 }, outlineLevel: 1 });
             if (this.report_data.doanh_thu_tien_internet_detail) this.visibleDetails(this.report_data.doanh_thu_tien_internet_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 2 }, outlineLevel: 2 }));

             // Motorbike
             data.push({ name: 'Doanh thu tiền xe máy', amount: this.report_data.doanh_thu_tien_xe_may, style: { indent: 1 }, outlineLevel: 1 });
             if (this.report_data.doanh_thu_tien_xe_may_detail) this.visibleDetails(this.report_data.doanh_thu_tien_xe_may_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 2 }, outlineLevel: 2 }));
             
             // Washing
             data.push({ name: 'Doanh thu tiền máy giặt', amount: this.report_data.doanh_thu_tien_may_giat, style: { indent: 1 }, outlineLevel: 1 });
             if (this.report_data.doanh_thu_tien_may_giat_detail) this.visibleDetails(this.report_data.doanh_thu_tien_may_giat_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 2 }, outlineLevel: 2 }));

             // Adjust Room
             data.push({ name: 'Điều chỉnh tiền phòng', amount: this.report_data.dieu_chinh_tien_phong, outlineLevel: 0 }); // Level 0?

             // Deposit
             data.push({ name: 'Doanh thu tiền cọc', amount: this.report_data.doanh_thu_tien_coc, outlineLevel: 0 });
             if (this.report_data.doanh_thu_tien_coc_detail) this.visibleDetails(this.report_data.doanh_thu_tien_coc_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 1 }, outlineLevel: 1 }));

             // Return Deposit 
             data.push({ name: 'Chi trả cọc', amount: this.report_data.chi_tra_coc, outlineLevel: 0 });
             if (this.report_data.chi_tra_coc_detail) this.visibleDetails(this.report_data.chi_tra_coc_detail).forEach(d => data.push({ name: d.text, amount: d.amount, style: { indent: 1 }, outlineLevel: 1 }));

             // Other
             data.push({ name: 'Thu khác', amount: this.report_data.thu_khac });

             // TOTAL REVENUE
             data.push({ name: 'TỔNG DOANH THU', amount: this.report_data.tong_doanh_thu, style: { bold: true, background: 'FFFFF3E0' } });




             // Helper for Expense (Negate positive expenses for display like in UI (100) -> -100)
             // But wait, in `formatExpenseValue`, positive input `num > 0` becomes `(num)`.
            data.push({ name: 'CHI PHÍ', style: { bold: true, background: 'FFFFF3E0' }, outlineLevel: 0 });

            // Helper for Expense (Negate positive expenses for display like in UI (100) -> -100)
            // But wait, in `formatExpenseValue`, positive input `num > 0` becomes `(num)`.
            // In Excel, negative number -100 is typically displayed as `(100)`.
            // So if DB value is positive (e.g. 5000), we should insert -5000.
            const addExpenseItem = (name, amount, details, level = 0) => {
                const displayAmount = (amount && amount > 0) ? -amount : (amount || 0);
                data.push({ name, amount: displayAmount, outlineLevel: level });
                if (details) {
                    this.visibleDetails(details).forEach(d => {
                        const dAmount = (d.amount && d.amount > 0) ? -d.amount : (d.amount || 0);
                        data.push({ name: d.text, amount: dAmount, style: { indent: level + 1 }, outlineLevel: level + 1 });
                        // Nested
                        if (d.details) {
                            d.details.forEach(sub => {
                                const subAmount = (sub.amount && sub.amount > 0) ? -sub.amount : (sub.amount || 0);
                                data.push({ name: sub.text, amount: subAmount, style: { indent: level + 2 }, outlineLevel: level + 2 });
                            });
                        }
                    });
                }
            };

             addExpenseItem('Chi phí lương và quản lý', this.report_data.chi_phi_luong_va_quan_ly, this.report_data.chi_phi_luong_detail);
             addExpenseItem('Chi phí thuê đất', this.report_data.chi_phi_thue_dat, this.report_data.chi_phi_thue_dat_detail);
             addExpenseItem('Chi phí điện', this.report_data.chi_phi_dien, this.report_data.chi_phi_dien_detail);
             addExpenseItem('Chi phí nước', this.report_data.chi_phi_nuoc, this.report_data.chi_phi_nuoc_detail);
             addExpenseItem('Chi phí rác, vệ sinh', this.report_data.chi_phi_rac_ve_sinh);
             addExpenseItem('Chi phí thuế', this.report_data.chi_phi_thue);
             addExpenseItem('Chi phí lãi vay', this.report_data.chi_phi_lai_vay, this.report_data.chi_phi_lai_vay_detail);
             addExpenseItem('Chi phí quảng cáo', this.report_data.chi_phi_quang_cao);
             addExpenseItem('Chi phí Internet', this.report_data.chi_phi_internet);
             addExpenseItem('Chi mua vật tư và sửa chữa', this.report_data.chi_mua_vat_tu_va_sua_chua, this.report_data.chi_mua_vat_tu_detail);
             addExpenseItem('Chi phí dẫn phòng', this.report_data.chi_phi_dan_phong);
             addExpenseItem('Chi phí khác', this.report_data.chi_phi_khac);

             // TOTAL EXPENSE
             // DB value is positive, so we negate it
             const totalExp = this.report_data.tong_chi_phi ? -this.report_data.tong_chi_phi : 0;
             data.push({ name: 'TỔNG CHI PHÍ', amount: totalExp, style: { bold: true, background: 'FFFFF3E0' } });

             // PROFIT
             data.push({ name: 'LỢI NHUẬN', amount: this.report_data.loi_nhuan, style: { bold: true, background: 'E3F2FD' } });
             
             // Margin
             // Pass as text to avoid currency formatting confusion
             const marginStr = this.report_data.ty_suat_loi_nhuan !== null ? `${this.report_data.ty_suat_loi_nhuan}%` : '-';
             data.push({ name: '%', amount: marginStr, style: { bold: true, background: 'E3F2FD', align: 'right' } });

             // --- ANALYSIS SECTION ---
             data.push({ name: '', amount: '' }); // Spacer
             data.push({ name: 'PHÂN TÍCH TỶ SUẤT LỢI NHUẬN', style: { bold: true, fontSize: 13 } });

             const analysis = this.report_data.phan_tich_ty_suat_loi_nhuan;
             if (analysis) {
                 const groups = [
                     { key: 'tien_phong', label: '1. Doanh thu tiền phòng' },
                     { key: 'xe_may', label: '2. Doanh thu tiền xe máy' },
                     { key: 'dien', label: '3. Doanh thu tiền điện' },
                     { key: 'nuoc', label: '4. Doanh thu tiền nước' },
                     { key: 'rac', label: '5. Doanh thu tiền rác' },
                     { key: 'internet', label: '6. Doanh thu tiền internet' }
                 ];

                  groups.forEach((g, index) => {
                      const item = analysis[g.key];
                      if (item) {
                          if (index > 0) data.push({ name: '', amount: '' }); // Spacer between sections
                          data.push({ name: g.label, amount: item.revenue, style: { bold: true, background: 'FFF3E0' } }); // Highlight header
                          data.push({ name: 'Chi phí', amount: item.expense ? -item.expense : 0, style: { indent: 1 }, outlineLevel: 0 });
                          data.push({ name: 'Net', amount: item.profit, style: { indent: 1 }, outlineLevel: 0 });
                          data.push({ name: '% Margin', amount: `${item.margin}%`, style: { indent: 1 }, outlineLevel: 0 });
                      }
                  });
             }

            await ExcelExporter.exportReport({
                title: 'BÁO CÁO KẾT QUẢ KINH DOANH',
                subtitle: `Tháng ${this.formatMonthDisplay(this.chosen_month)}`,
                columns: [
                    { header: 'Chỉ tiêu', key: 'name', width: 40 },
                    { header: 'Số tiền', key: 'amount', width: 20, align: 'right', format: 'currency' }
                ],
                data: data,
                fileName: `BaoCaoKQKD_${this.chosen_month}`
            });

        } catch (error) {
            console.error(error);
            swal.fire("Lỗi", "Không thể xuất file Excel", "error");
        }
        this.exporting = false;
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.business-report-table {
  width: 100%;
  border-collapse: collapse;
}

.business-report-table th,
.business-report-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.business-report-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.business-report-table .section-header {
  background-color: #e3f2fd;
}

.business-report-table .total-row {
  background-color: #fff3e0;
}

.business-report-table .profit-row {
  background-color: #e8f5e9;
}

.business-report-table .sub-row {
  background-color: #fafafa;
}

.business-report-table .sub-row.level-1 {
  background-color: #f5f5f5;
}

.business-report-table .sub-row.level-2 {
  background-color: #fafafa;
}

.business-report-table .expandable-row {
  cursor: pointer;
}

.business-report-table .expandable-row:hover,
.business-report-table .sub-row.level-1:hover {
  background-color: #eeeeee;
}

.business-report-table tbody tr:hover {
  background-color: #fafafa;
}

.pl-3 {
  padding-left: 24px !important;
}

.pl-5 {
  padding-left: 40px !important;
}

.negative-value {
  color: #d32f2f;
}

.positive-value {
  color: #388e3c;
}

.margin-table th {
  background-color: #e8f5e9;
}

.margin-table .margin-header-row {
  background-color: #e3f2fd;
  border-top: 2px solid #2196f3;
}

.margin-table .margin-header-row td:first-child {
  vertical-align: middle;
  text-align: center;
  font-weight: 600;
}
</style>
